import React from "react";

import { useLiveUpdates } from "@hooks/useLiveUpdates";
import { IdTooltip } from "@src/components/elements/IdTooltip";
import { buildThemeClasses } from "@src/utils/theme";
import { getContentfulPreviewPropsFn } from "@utils";
import clsx from "clsx";
import { graphql } from "gatsby";

function getHeroImage(entry) {
  if (entry.variant === 'image-full') {
    return entry.heroImage?.backgroundImage?.src ?? entry.heroImage?.file?.url;
  }

  return entry.heroImage?.sideImage?.src ?? entry.heroImage?.file?.url;
}

export function SectionHero({ entry: rawEntry, isGrt = false }) {
  const entry = useLiveUpdates(rawEntry);
  const getHeroPreviewProps = getContentfulPreviewPropsFn(entry.contentful_id);
  const getPrimaryCtaPreviewProps = getContentfulPreviewPropsFn(
    entry.primaryCta?.contentful_id
  );
  const getSecondaryCtaPreviewProps = getContentfulPreviewPropsFn(
    entry.secondaryCta?.contentful_id
  );
  const heroImage = getHeroImage(entry);
  const {overline} = entry;
  // Find the registered trademark symbol and wrap it in a <sup> tag
  const sup = (str, find = "\u00AE") => {
    if (!str) return null;
    if (!str.includes(find)) return str;

    return str.split(find).reduce((acc, part, idx) => {
      if (idx === 0) return acc + part;

      return (
        <>
          {acc}
          {/* eslint-disable-next-line react/no-danger */}
          <sup dangerouslySetInnerHTML={{__html: find }} />
          {part}
        </>
      );
    }, '');
  }
  const headline = entry.headline?.split("*").map((word, idx) =>
    idx % 2 ? (
      <span
        key={word}
        className="text-emphasis"
      >
        {sup(word)}
      </span>
    ) : (
      sup(word)
    )
  );

  const primaryCta = useLiveUpdates(entry.primaryCta);
  const secondaryCta = useLiveUpdates(entry.secondaryCta);
  const {variant} = entry;
  const isImageRight = variant === 'image-right';
  const isImageSide = variant === 'image-left' || variant === 'image-right';

  return (
    <section
      className={clsx(
        "relative overflow-hidden font-atkinson",
        isImageSide ? "bg-accent-2 py-section" : "bg-main-gradient",
        {...buildThemeClasses(entry?.theme)}
      )}
      data-theme={rawEntry?.theme}
      id={entry.contentful_id}
    >
      <IdTooltip id={entry.contentful_id} />
      <div
        className={clsx(
          isImageSide ? "inner flex flex-col md:grid md:grid-cols-2 gap-20" : ""
        )}
      >
        {heroImage && (
          <div
            className={clsx(
              isImageSide && "flex items-center",
              isImageRight && "col-start-2 col-end-3 row-start-1 row-end-2"
            )}
          >
            <img
              alt=""
              aria-hidden="true"
              className={clsx(
                !isImageSide && "absolute w-full object-cover h-full",
                isImageSide && "w-full object-contain h-auto"
              )}
              height={isImageSide ? "375" : "750"}
              src={`${
                heroImage.indexOf("https:") !== -1 ? "" : "https:"
              }${heroImage}`}
              width={isImageSide ? "720" : "1440"}
            />
          </div>
        )}

        <div
          className={clsx(
            isImageSide && "flex items-center",
            isImageRight && "col-start-1 col-end-2 row-start-1 row-end-2"
          )}
          {...getHeroPreviewProps("heroImage")}
        >
          <div
            className={clsx(
              "flex flex-col relative text-center justify-center items-center",
              !isImageSide && "inner-smaller min-h-480px",
              isImageSide && "md:justify-start md:items-start md:text-left"
            )}
          >
            {overline && <p {...getHeroPreviewProps("overline")}>{overline}</p>}
            {headline && (
              <h1
                className="text-h1 mt-3 mb-7"
                {...getHeroPreviewProps("headline")}
              >
                {headline}
              </h1>
            )}
            {entry.subtitle?.subtitle && (
              <p className="mb-8">{entry.subtitle.subtitle}</p>
            )}

            <div
              className={clsx("flex-col md:flex-row flex gap-3", {
                "gap-8 md:gap-3 justify-center": isGrt,
                "justify-center w-full max-w-sm md:max-w-full": !isGrt && !isImageSide,
                "justify-center md:justify-start w-full max-w-sm md:max-w-full":
                  !isGrt && isImageSide,
              })}
            >
              {primaryCta && (
                <a
                  className="btn-new justify-center"
                  href={primaryCta.url}
                  {...getHeroPreviewProps("primaryCta")}
                >
                  <span {...getPrimaryCtaPreviewProps("label")}>
                    {primaryCta.label}
                  </span>

                  {/**
                   * A concession to the fact that the GRT page is slightly different from the
                   * rest of the pages, but close enough that we don't want to make a separate component.
                   * This adds a download icon to the primary CTA on the GRT page.
                   */}
                  {isGrt && (
                    <svg
                      fill="none"
                      height="20"
                      viewBox="0 0 20 20"
                      width="20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        fill="white"
                        fillOpacity="0.01"
                        height="20"
                        width="20"
                      />
                      <path
                        clipRule="evenodd"
                        d="M9.99942 1.3999C10.3308 1.3999 10.5994 1.66853 10.5994 1.9999V11.218L13.5752 8.2423C13.8095 8.00798 14.1893 8.00798 14.4236 8.2423C14.658 8.47662 14.658 8.85651 14.4236 9.09084L10.4237 13.0908C10.1894 13.3251 9.80947 13.3251 9.57515 13.0908L5.57515 9.09084C5.34083 8.85651 5.34083 8.47662 5.57515 8.2423C5.80947 8.00798 6.18936 8.00798 6.42368 8.2423L9.39942 11.218V1.9999C9.39942 1.66853 9.66804 1.3999 9.99942 1.3999ZM3.33268 13.3332C3.70087 13.3332 3.99935 13.6318 3.99935 13.9999V15.9999C3.99935 16.7384 4.59355 17.3332 5.32782 17.3332H14.6676C15.4032 17.3332 15.9993 16.737 15.9993 15.9999V13.9999C15.9993 13.6318 16.2979 13.3332 16.666 13.3332C17.0341 13.3332 17.3327 13.6318 17.3327 13.9999V15.9999C17.3327 17.472 16.1409 18.6666 14.6676 18.6666H5.32782C3.85294 18.6666 2.66602 17.4706 2.66602 15.9999V13.9999C2.66602 13.6318 2.9645 13.3332 3.33268 13.3332Z"
                        fill="white"
                        fillRule="evenodd"
                      />
                    </svg>
                  )}
                </a>
              )}
              {secondaryCta && (
                <a
                  className={clsx('justify-center', {
                    "text-new-green md:hidden": isGrt,
                    "btn-new btn-new--outline": !isGrt,
                  })}
                  href={secondaryCta.url}
                  {...getHeroPreviewProps("secondaryCta")}
                >
                  <span {...getSecondaryCtaPreviewProps("label")}>
                    {secondaryCta.label}
                  </span>
                  {isGrt && (
                    <svg
                      className="inline-block ml-2"
                      fill="none"
                      height={20}
                      width={21}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        clipRule="evenodd"
                        d="M5.638 2.862a.667.667 0 1 0-.943.943l5.334 5.333c.26.26.682.26.942 0l5.334-5.333a.667.667 0 0 0-.943-.943L10.5 7.724 5.638 2.862Zm0 8a.667.667 0 1 0-.943.943l5.334 5.333c.26.26.682.26.942 0l5.334-5.333a.667.667 0 0 0-.943-.943L10.5 15.724l-4.862-4.862Z"
                        fill="#008347"
                        fillOpacity={0.84}
                        fillRule="evenodd"
                      />
                    </svg>
                  )}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const HeroCTA = graphql`
  fragment HeroCTA on ContentfulLink {
    contentful_id
    __typename
    sys {
      id
    }
    url
    label
  }
`;

export const HeroImage = graphql`
  fragment HeroImage on ContentfulAsset {
    backgroundImage: resize(width: 1440, height: 750, quality: 100) {
      src
    }
    sideImage: resize(width: 635, height: 455, quality: 100) {
      src
    }
    file {
      url
      contentType
    }
  }
`;

export const SectionHeroFragment = graphql`
  fragment SectionHero on ContentfulSectionHero {
    contentful_id
    __typename
    sys {
      id
    }
    theme
    variant
    headline
    subtitle {
      subtitle
    }
    overline
    heroImage {
      ...HeroImage
    }
    primaryCta {
      ...HeroCTA
    }
    secondaryCta {
      ...HeroCTA
    }
  }
`;
